import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Filter, Package, ArrowRight, Tag, Warehouse,
  ArrowUpDown, ChevronLeft, ChevronRight, Globe, Building2, X, RefreshCw, Box, ChevronsLeft, ChevronsRight, Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme } from '../lib/swalTheme';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

// استيراد السياقات الموحدة
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';
import { exportToExcel } from './exportUtils';
import SmartPrintButton from './PrintButton';
import { useFavoriteFilters } from './useFavoriteFilters';

const InventoryList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // جلب معلومات الجلسة والصلاحيات
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const { isSuperAdmin } = usePermissions('inventory');
  
  // سياق حجم الخط
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // --- States ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const itemnum = searchParams.get('itemnum') || "";
  const locationFilter = searchParams.get('location') || "";
  const status = searchParams.get('status') || "";
  const sortBy = searchParams.get('sortBy') || "itemnum";
  const sortDir = searchParams.get('sortDir') || "asc";
  const selectedOrg = searchParams.get('org_id') || "";
  const selectedSite = searchParams.get('site_id') || "";
  const showAllSites = searchParams.get('all_sites') === 'true';

  // --- Dropdowns for Super Admin ---
  const [orgsList, setOrgsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);

  // 🎯 دالة جلب البيانات مع الفلاتر والباجينيشن
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const forceAll = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
      const effectiveAll = showAllSites || forceAll;
      const effectiveSite = effectiveAll ? selectedSite : sessionSite;

      const params = {
        page, limit, q, itemnum, location: locationFilter, status,
        site_id: effectiveSite, org_id: selectedOrg || sessionOrg,
        all_sites: Boolean(effectiveAll), sortBy, sortDir
      };

      const res = await axios.get('/api/inventory/all', { headers: { 'Authorization': `Bearer ${token}` }, params });
      setData(res.data.data || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      toast.error(t('common.load_error', "Failed to sync inventory data"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, itemnum, locationFilter, status, selectedOrg, selectedSite, showAllSites, sortBy, sortDir, isSuperAdmin, sessionSite, sessionOrg, t]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  // جلب المنظمات للمدير العام
  useEffect(() => {
    if (isSuperAdmin) {
      axios.get('/api/organizations', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(res => setOrgsList(res.data));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (selectedOrg) {
      const org = orgsList.find(o => o.org_id === selectedOrg);
      setSitesList(org ? org.sites : []);
    } else { setSitesList([]); }
  }, [selectedOrg, orgsList]);

  // --- Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleClearFilters = () => {
    setSearchParams({});
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const { saveFavorite } = useFavoriteFilters('INVENTORY');
  const handleSaveFavoriteFilter = async () => {
    const { value: name } = await Swal.fire({
      ...swalTheme(),
      title: t('favorites.namePrompt', 'Name this favorite filter'),
      input: 'text',
      inputPlaceholder: t('favorites.namePlaceholder', 'e.g. My Open WOs'),
      showCancelButton: true,
      confirmButtonText: t('common.save', 'Save'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      inputValidator: (value) => !value?.trim() && t('favorites.nameRequired', 'Please enter a name')
    });
    if (name && name.trim()) {
      saveFavorite(name, {
        q, itemnum, location: locationFilter, status,
        org_id: selectedOrg, site_id: selectedSite, all_sites: showAllSites
      });
    }
  };

  useEffect(() => {
    const applyFavorite = (e) => {
      if (e.detail?.appName === 'INVENTORY' && e.detail?.filters) {
        updateFilters({ ...e.detail.filters, page: 1 });
      }
    };
    window.addEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
    return () => window.removeEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
  }, [searchParams]);

  // تحضير الكويري للانتقال للديتيلز
  const handleRowClick = (itemnum, location_id) => {
    const queryString = new URLSearchParams(Object.fromEntries(searchParams.entries())).toString();
    navigate(`/inventory/${itemnum}/${location_id}${queryString ? '?' + queryString : ''}`);
  };

  // --- Sidebar Events ---
  useEffect(() => {
      const openAllSites = () => updateFilters({ page: 1, all_sites: true });
      const viewMySite = () => updateFilters({ page: 1, all_sites: false });
      
      const exportData = () => {
          const exportParams = Object.fromEntries(searchParams.entries());
          const columnsMap = {
              "itemnum": t('inventory.fields.itemnum', 'Item #'),
              "description": t('common.description', 'Description'),
              "location": t('inventory.fields.location', 'Storeroom'),
              "curbal": t('inventory.fields.balance', 'Balance'),
              "avgcost": t('inventory.fields.avgcost', 'Avg Cost'),
              "status": t('common.status', 'Status')
          };
          exportToExcel('inventory', exportParams, columnsMap, t);
      };

      window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.addEventListener('GLOBAL_EXPORT', exportData);
      
      return () => {
          window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
          window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
          window.removeEventListener('GLOBAL_EXPORT', exportData);
      };
  }, [searchParams, t]);

  const totalPages = Math.ceil(totalItems / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter font-bold ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* 1. Header & Global Search */}
      <div className="bg-surface-card p-3 border-b shadow-sm z-20 space-y-3">
        <div className="flex justify-between items-center px-4">
          <div className="flex items-center gap-6">
            <h2 className="text-primary font-black italic text-xl flex items-center gap-2">
              <RefreshCw size={22} className={`${loading ? 'animate-spin text-primary' : 'text-primary cursor-pointer hover:rotate-180 transition-all duration-500'}`} onClick={() => fetchInventory()} title={t('common.refresh', 'Refresh')} />
              {t('inventory.list.title', 'Inventory Registry')}
              <span className={`text-[10px] px-2 py-0.5 rounded-full border not-italic font-bold ml-2 ${showAllSites ? 'bg-status-danger-bg text-status-danger border-status-danger/30' : 'bg-primary-soft text-primary border-primary/30'}`}>
                  {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
              </span>
            </h2>
            <div className={`relative group ${isRTL ? 'mr-4' : 'ml-4'}`}>
              <Search className={`absolute top-2.5 text-ink-secondary group-focus-within:text-primary ${isRTL ? 'right-3' : 'left-3'}`} size={16} />
              <input
                className={`py-2 border border-line w-[400px] outline-none focus:border-primary shadow-inner rounded-sm font-black text-primary placeholder:font-normal placeholder:italic uppercase transition-all ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'}`}
                placeholder={t('common.search')} 
                value={q}
                onChange={(e) => updateFilters({ q: e.target.value, page: 1 })}
              />
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <SmartPrintButton moduleName="INVTRANS" />
            <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
              <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>
            <button
              onClick={handleSaveFavoriteFilter}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-control border border-line bg-surface-panel text-status-warning text-[10px] font-bold uppercase hover:bg-primary-soft transition-all"
            >
              <Star size={13} /> {t('favorites.save', 'SAVE AS FAVORITE')}
            </button>
            {/* Org/Site Filters for SuperAdmin */}
            {isSuperAdmin && (
              <div className="flex items-center gap-4 bg-surface-panel p-2 rounded-sm border border-line shadow-sm animate-in fade-in">
                <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-l pl-4' : 'border-r pr-4'}`}>
                  <Globe size={14} className="text-primary" />
                  <select className={`bg-transparent outline-none text-[10px] font-black uppercase cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`} value={selectedOrg} onChange={(e) => updateFilters({ org_id: e.target.value, site_id: '', page: 1 })}>
                    <option value="">{t('common.all_orgs', 'All Orgs')}</option>
                    {orgsList.map(o => <option key={o.org_id} value={o.org_id}>{o.org_id}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-primary" />
                  <select className={`bg-transparent outline-none text-[10px] font-black uppercase cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`} value={selectedSite} onChange={(e) => updateFilters({ site_id: e.target.value, page: 1 })} disabled={!selectedOrg}>
                    <option value="">{t('common.all_sites', 'All Sites')}</option>
                    {sitesList.map(s => <option key={s.site_id} value={s.site_id}>{s.site_id}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. Grid Content */}
      <div className="flex-1 p-4 overflow-auto custom-scrollbar">
        <div className="bg-surface-card border shadow-sm rounded-sm overflow-hidden min-w-max min-h-[450px]">
          <table className="w-full text-left">
            <thead className="bg-primary-dark text-white font-black uppercase sticky top-0 z-10 tracking-widest">
              <tr>
                <SortableHeader label={t('inventory.fields.itemnum', 'Item #')} sortKey="itemnum" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.desc', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('inventory.fields.location', 'Storeroom')} sortKey="location" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('inventory.fields.balance', 'Balance')} sortKey="curbal" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" isRTL={isRTL} />
                <SortableHeader label={t('inventory.fields.avgcost', 'Avg Cost')} sortKey="avgcost" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.status', 'Status')} sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
              </tr>
              {/* Column Filters Row */}
              <tr className="bg-surface-panel shadow-inner">
                <td className="p-1 border-x border-line">
                  <FilterInput value={itemnum} onChange={v => updateFilters({ itemnum: v, page: 1 })} />
                </td>
                <td className="p-1 border-x border-line bg-surface-panel/50"></td>
                <td className="p-1 border-x border-line">
                  <FilterInput value={locationFilter} onChange={v => updateFilters({ location: v, page: 1 })} />
                </td>
                <td className="p-1 border-x border-line bg-surface-panel/50"></td>
                <td className="p-1 border-x border-line bg-surface-panel/50"></td>
                <td className="p-1 border-x border-line">
                  <select className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent cursor-pointer ${isRTL ? 'text-right' : 'text-center'}`} value={status} onChange={e => updateFilters({ status: e.target.value, page: 1 })}>
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="PENDING">PENDING</option>
                  </select>
                </td>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {loading ? (
                <tr><td colSpan="6" className="p-32 text-center italic text-primary font-black animate-pulse tracking-[0.5em]">{t('common.syncing', 'Synchronizing Inventory Node...')}</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="6" className="p-32 text-center text-ink-secondary italic font-black tracking-widest">{t('common.no_records', 'No Stock Records Found Matching Criteria')}</td></tr>
              ) : data.map((row, idx) => (
                <tr
                  key={`${row.itemnum}-${row.location}`}
                  onClick={() => handleRowClick(row.itemnum, row.location)}
                  className={`hover:bg-primary-soft cursor-pointer group transition-all text-[11px] ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <td className={`p-4 border-line-subtle text-primary font-black flex items-center gap-2 group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>
                    <Tag size={12} className="text-primary/60"/> {row.itemnum}
                  </td>
                  <td className={`p-4 border-line-subtle text-ink italic font-bold truncate max-w-sm ${isRTL ? 'border-l' : 'border-r'}`}>{row.description || '---'}</td>
                  <td className={`p-4 border-line-subtle uppercase ${isRTL ? 'border-l' : 'border-r'}`}><Warehouse size={12} className={`inline text-ink-secondary ${isRTL ? 'ml-2' : 'mr-2'}`}/> {row.location || '---'}</td>
                  <td className={`p-4 border-line-subtle text-status-success font-mono text-sm bg-status-success-bg/20 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{parseFloat(row.curbal || 0).toFixed(2)}</td>
                  <td className={`p-4 border-line-subtle font-mono text-primary text-sm ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{parseFloat(row.avgcost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                  <td className="p-4 text-center">
                    <span className={`px-4 py-1 rounded-sm text-[9px] font-black shadow-sm ring-1 ring-inset ${row.status === "ACTIVE" ? 'bg-status-success-bg text-status-success ring-status-success/20' : 'bg-status-neutral-bg text-status-neutral ring-status-neutral/20'}`}>
                      {row.status || 'DRAFT'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3. Pagination Footer */}
      <div className={`bg-surface-card border-t border-line p-2 px-8 flex justify-between items-center shadow-sm z-30 font-black text-[10px] text-ink-secondary rounded-b-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Box size={14} className="text-primary"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, totalItems)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-status-warning"/>
                <span>{t('common.total', 'Total')}: <span className="text-status-warning">{totalItems}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-1 bg-surface-panel rounded px-2 py-1 border border-line ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[11px] font-mono border-x border-line mx-1">
                    <span className="text-primary text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-4 border-x border-white/10 cursor-pointer hover:bg-black/20 transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} `}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-status-warning' : 'opacity-30'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder = "", center = false, className = "" }) => (
  <input
    className={`w-full p-1 text-primary font-bold outline-none bg-transparent placeholder-ink-secondary border border-line rounded-sm focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all ${center ? 'text-center' : ''} ${className}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
  />
);

export default InventoryList;